import "./style.scss";

import { Close } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { Avatar, Collapse, Drawer, Modal, Skeleton, Tab } from "@mui/material";
import Basket from "assets/img/svg/basket";
import DoubleArrow from "assets/img/svg/doubleArrow";
import ModalBox from "components/Modal";
import ExitModal from "components/NewForm/ExitModal";
import TermRecap from "components/TermRecap";
import type { NewAgreementLocationState } from "containers/ContractValidationNew";
import type { ChannelParams } from "containers/TermFormContainer";
import { coverUrlForWork, workPreviewFromTerm } from "containers/Works/workPreview";
import TermFormContainer, { ChannelData } from "containers/TermFormContainer";
import { resolveCalculationBaseColumn } from "containers/TermFormContainer/ChannelsStep/calculationBaseColumnUtils";
import { resolveMilestoneBaseColumn } from "containers/TermFormContainer/ChannelsStep/milestoneBaseColumnUtils";
import { getGroupWithChannels, splitDefaultConfigurationFlag } from "containers/TermFormContainer/utils";
import { Body, H3, SmallBody } from "helpers/TypographyComponents";
import { isAgreementOfTerm } from "helpers/termAgreements";
import { formatLocaleDateString, getInitials, isObjectEmpty } from "helpers/utils";
import { useActiveSignProvider } from "hooks/useActiveSignProvider";
import { useSignatureProvider } from "hooks/useSignatureProvider";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchAgreementGenerated, fetchAgreements } from "store/agreementsStore";
import {
    addNoteToAssociatedTerm as addNoteToAssociatedTermBeneficiary,
    deleteNoteFromAssociatedTerm as deleteNoteFromAssociatedTermBeneficiary,
    fetchAssociatedPersons,
    fetchBeneficiar,
    fetchOneTermGroup,
} from "store/beneficiaryStore";
import { fetchChannelCompensationGroups } from "store/channelStore";
import type { INotes } from "store/notesStore";
import { deleteNote, fetchNotes, postNotes } from "store/notesStore";
import type { AppDispatch } from "store/store";
import { RootState, useAppSelector } from "store/store";
import {
    addNoteToAssociatedTerm as addNoteToAssociatedTermTitle,
    deleteNoteFromAssociatedTerm as deleteNoteFromAssociatedTermTitle,
    fetchTitle,
} from "store/titleStore";
import { fetchWork, fetchWorkTerms } from "store/workStore";

import variables from "../../../assets/styles/_variables.module.scss";
import AgreementModal, { VARIABLE_REGEX } from "../AgreementModal";
import ButtonBase from "../ButtonBase";
import type { Action } from "../ButtonMenu";
import ButtonMenu from "../ButtonMenu";
import ContractsCounterBlock from "../ContractsCounterBlock";
import DeleteModal from "../DeleteModal";
import InfoBlue from "../InfoBlockBlue";
import RichEditor from "../RichEditor";
import { Spinner } from "../Spinner";
import StyledButton from "../StyledButton";
import { StyledDatePicker } from "../StyledDatePicker";
import { StyledTabs } from "../StyledTabs";
import StyledTextField from "../StyledTextField/StyledTextField";
import TermDetail from "../TermDetail";
import DropZoneCSV from "../UploadCsv";
import Plus from "./../../../assets/img/svg/plus";
import type { ITerm } from "./../../../store/managementStore";
import {
    clearCurrentTerm,
    fetchAdvancePaymentsOfTerm,
    fetchTerm,
    sendManualContractBook,
} from "./../../../store/termStore";

const ShowDetails = ({
    setOpen,
    isOpen,
    currentTerm,
    deleteTerm = undefined,
    from,
    company,
    currentTitle,
    currentBeneficiary,
    advancePaymentId,
}: any) => {
    const [t] = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const { term } = useAppSelector((state) => state.term);
    const { notes } = useAppSelector((state) => state.notes);
    const { channels, channelCompensationGroups } = useAppSelector((state) => state.channels);
    const { agreementGenerated, agreements } = useAppSelector((state) => state.agreements);
    const { sendAgreementStatus, agreementsStatus } = useAppSelector((state) => state.agreements.agFetchStatus);
    const { beneficiar, associatedPersons, termGroup, associatedPersonsLoadStatus } = useAppSelector(
        (state) => state.beneficiary,
    );
    const { title } = useAppSelector((state) => state.titles);
    const { selectedWork, workTermsStatus, workTermsWorkId } = useAppSelector((state) => state.works);
    const { termAdvancePayments, termStatus, termAdvanceStatus } = useAppSelector((state) => state.term);

    const [currentTab, setCurrentTab] = useState(0);
    const [isAddingNote, setIsAddingNote] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [blockModal, setBlockModal] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [showDocumentSign, setShowDocumentSign] = useState(false);
    const [showAgreementModal, setShowAgreementModal] = useState(false);
    const [manualUpload, setManualUpload] = useState(false);
    const [signatureDate, setSignatureDate] = useState(new Date());
    const [file, setFile] = useState<any>();
    const [deleteNoteModal, setDeleteNoteModal] = useState(false);
    const [noteToDeleteId, setNoteToDeleteId] = useState<number | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [chosenChannels, setChosenChannels] = useState<ChannelParams[]>([]);

    const activeSignProvider = useActiveSignProvider();
    const { isDocoon } = useSignatureProvider(activeSignProvider.key);

    const { getCompanySettings } = useCompanySettings();
    const usesAgreements = getCompanySettings("uses_agreements");
    const usesAgreementValidation = getCompanySettings("uses_agreement_validation_chain");
    const usesChannelGroups = getCompanySettings("uses_interchannel_compensation");
    const usesAdvanceGroups = getCompanySettings("uses_interterm_compensation");
    const usesInitialAdvance = getCompanySettings("uses_initial_advance");
    const usesCumulativeRoyalties = getCompanySettings("uses_cumulative_royalties");

    const validationList = JSON.parse(localStorage.getItem("validation-list") ?? "{}");
    const termValidationData = term?.termId && validationList[term?.termId];

    const termAgreements = useMemo(
        () =>
            agreements.filter((a) =>
                isAgreementOfTerm(a, {
                    termId: term.termId ?? term.id,
                    workId: term.workId,
                    titleId: term.titleId,
                    beneficiaryId: term.beneficiarId,
                }),
            ),
        [agreements, term.termId, term.id, term.workId, term.titleId, term.beneficiarId],
    );

    const agreementGeneratedWithUserVariables = useMemo(() => {
        if (!agreementGenerated) return "";
        if (!termValidationData) return "";
        const variables: any = [];
        for (const match of agreementGenerated.matchAll(VARIABLE_REGEX)) {
            variables.push({
                variable: match[0],
                group: match[1],
                index: match.index,
            });
        }
        let localAgreementGenerated = agreementGenerated;
        for (const variable of variables) {
            localAgreementGenerated = localAgreementGenerated.replaceAll(
                variable.variable,
                termValidationData.userVariables[variable.group],
            );
        }
        return localAgreementGenerated;
    }, [agreementGenerated, termValidationData]);

    const groupsWithChannels = getGroupWithChannels(channelCompensationGroups, chosenChannels);

    const channelGroupIds = channelCompensationGroups.map((group) => group.id);

    const filteredChannels = useMemo(() => {
        const channels = chosenChannels ? chosenChannels : [];
        return usesChannelGroups
            ? channels.filter((channel) => !channelGroupIds.includes(channel?.groupId ?? -1))
            : channels;
    }, [chosenChannels, usesChannelGroups, channelGroupIds]);

    const channelsInGroups = useMemo(() => {
        const channels = chosenChannels ? chosenChannels : [];
        return usesChannelGroups
            ? channels.filter((channel) => channelGroupIds.includes(channel?.groupId ?? -1))
            : chosenChannels;
    }, [chosenChannels, channelGroupIds, usesChannelGroups]);

    useEffect(() => {
        if (!from && term?.beneficiarId) {
            dispatch(fetchBeneficiar(term.beneficiarId));
            if (term.workId) dispatch(fetchWork(term.workId));
            else if (term.titleId) dispatch(fetchTitle(term.titleId.toString()));
        }

        // Parse data from an active channel, so I can use it in the Channels Step.
        if (term) {
            const parsedChannels = term.channelData?.map((channel) => {
                // Base and milestone columns are contract-scoped: fall back to the channel defaults
                // only when the contract has no stored value yet.
                const currentChannel = channels?.find((item) => item.id === channel.channelId);
                const calculationBaseColumn = currentChannel
                    ? resolveCalculationBaseColumn(currentChannel, channel.calculationBaseColumn)
                    : channel.calculationBaseColumn;
                const milestoneBaseColumn = resolveMilestoneBaseColumn(
                    channel.milestoneBaseColumn,
                    calculationBaseColumn ?? "quantity",
                );
                // Milestone thresholds are stored in the unit of their column: monetary ones in cents.
                const milestoneThresholdDivider = milestoneBaseColumn === "quantity" ? 1 : 100;

                const groupId = channels.find((item) => item.id === channel.channelId)?.compensationGroupId;
                let rawMilestones: unknown = channel.data;
                if (typeof channel.data === "string") {
                    try {
                        rawMilestones = JSON.parse(channel.data);
                    } catch {
                        return null;
                    }
                }
                if (!Array.isArray(rawMilestones)) return null;
                const { milestones, showDefaultConfiguration } = splitDefaultConfigurationFlag(
                    rawMilestones as ChannelData[],
                );
                const newData = milestones.map((item) => ({
                    ...item,
                    quantity: +item.quantity / milestoneThresholdDivider,
                }));
                return {
                    ...channel,
                    groupId: usesChannelGroups ? groupId ?? undefined : -1,
                    data: newData,
                    calculationBaseColumn,
                    milestoneBaseColumn,
                    formatTypeId: channel.formatTypeId ?? null,
                    showDefaultConfiguration,
                };
            });

            setChosenChannels((parsedChannels ?? []).filter((channel) => channel != null));
        }
    }, [term, usesChannelGroups]);

    useEffect(() => {
        if (associatedPersonsLoadStatus === "idle" && !isObjectEmpty(beneficiar)) {
            dispatch(fetchAssociatedPersons(beneficiar.id));
        }
    }, [associatedPersonsLoadStatus, beneficiar]);

    const chosenRepresentative = associatedPersons?.find((person) => person.id === term.representativeId);

    const dataForRecap = {
        title: from === "titles" ? currentTitle : title,
        work: workPreviewFromTerm(term?.workId != null ? term : currentTerm, selectedWork),
        beneficiary: from === "beneficiars" ? currentBeneficiary : beneficiar,
        reference: term?.reference ?? "",
        initialAdvance: term?.initialAdvance / 100 || "",
        endDate: term?.endDate ?? null,
        advancePayment: term?.advancePayment / 100 || 0,
        initialProvision: term?.initialProvision && (term?.initialProvision / 100 || 0),
        representative: chosenRepresentative ?? null,
        compensate: term?.compensate,
        cumulativeRights: term?.cumulativeRights && (term?.cumulativeRights / 100 || 0),
    };

    /**
     * Advance payment linked on creation of term.
     */
    const linkedAdvancePayment = useMemo(() => {
        const payment = termAdvancePayments.find(
            (payment) => payment.id === (term.advancePaymentId ?? advancePaymentId),
        );
        return payment;
    }, [termAdvancePayments, term]);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const handleCloseWithModal = () => {
        if (isDirty) setBlockModal(true);
        else setEditMode(false);
    };

    const onEditorChange = (e: any) => {
        // setNotes((oldNotes: any) => [...oldNotes, e])
        const linesToIgnore = [0];
        const title =
            e.blocks[0].text ||
            e.blocks.find((item: any, i: number) => {
                linesToIgnore.push(i);
                return item.text != "";
            }).text;
        e.blocks = e.blocks.filter((_: any, i: number) => !linesToIgnore.includes(i));
        const data: INotes = {
            title,
            description: JSON.stringify(e),
        };
        dispatch(postNotes({ termId: term.termId!, data }));
        if (from == "beneficiars") dispatch(addNoteToAssociatedTermBeneficiary({ termId: term.termId }));
        else dispatch(addNoteToAssociatedTermTitle({ termId: term.termId }));
        setIsAddingNote(false);
    };

    const handleAddingNote = () => setIsAddingNote(true);

    const handleOpenDeleteNoteModal = (noteId: number) => {
        setDeleteNoteModal(true);
        setNoteToDeleteId(noteId);
    };

    const handleDeleteNote = async () => {
        await dispatch(deleteNote({ termId: term.termId!, noteId: noteToDeleteId ?? -1 }));
        if (from == "beneficiars") dispatch(deleteNoteFromAssociatedTermBeneficiary({ termId: term.termId }));
        else dispatch(deleteNoteFromAssociatedTermTitle({ termId: term.termId }));
        setDeleteNoteModal(false);
    };

    const handleDeleteTerm = (termId: number) => {
        setEditMode(false);
        deleteTerm(termId);
        setOpen(false);
    };

    const handleClosingDrawer = () => {
        if (!editMode) {
            setOpen(false);
        }
        editMode && handleCloseWithModal();
    };

    const handleShowAgreementModal = () => {
        if (usesAgreementValidation || isDocoon) {
            const {
                beneficiarId: beneficiaryId,
                title,
                id,
                titleId,
                workId,
                author,
                firstname,
                lastname,
            } = currentTerm;
            const beneficiary =
                author ||
                [firstname, lastname].filter(Boolean).join(" ") ||
                [currentBeneficiary?.name, currentBeneficiary?.surname].filter(Boolean).join(" ");
            const state = {
                relatedResourceData: {
                    relatedResource: "term",
                    relatedResourceId: id,
                    beneficiaryId,
                    titleId,
                    workId: workId ?? term?.workId,
                    title,
                    beneficiary,
                },
                signatureProvider: activeSignProvider.key,
            } as NewAgreementLocationState;
            navigate("/agreements/new", { state });
            return;
        }
        setShowAgreementModal(true);
    };

    const handleShowUploadModal = () => {
        setShowDocumentSign(true);
        setManualUpload(true);
    };

    const sendForSignature = async () => {
        setIsLoading(true);
        if (!currentTerm) return;
        if (manualUpload) {
            if (file && file.length > 0 && signatureDate) {
                const formData = new FormData();
                formData.append("file", file[0], file[0].name);
                const response = await dispatch(
                    sendManualContractBook({
                        termId: currentTerm.id,
                        file: formData,
                        signatureDate,
                    }),
                );
                if (response.meta.requestStatus === "fulfilled") {
                    await dispatch(fetchTerm(currentTerm.id));
                }
            }
        }
        setShowDocumentSign(false);
        setFile([]);
        setIsLoading(false);
    };

    useEffect(() => {
        if (currentTerm) {
            dispatch(fetchTerm(currentTerm.id));
            dispatch(fetchNotes(currentTerm.id));
        }
        return () => {
            dispatch(clearCurrentTerm());
        };
    }, [currentTerm?.id, dispatch]);

    useEffect(() => {
        !channelCompensationGroups.length && dispatch(fetchChannelCompensationGroups());
        dispatch(fetchAgreements());
    }, []);

    useEffect(() => {
        if (!term?.id) return;

        if (term?.compensationGroupId) {
            dispatch(
                fetchOneTermGroup({
                    beneficiaryId: term?.beneficiarId,
                    groupId: term?.compensationGroupId,
                }),
            );
        }

        if (term.termId) {
            dispatch(fetchAdvancePaymentsOfTerm({ termId: term.termId }));
        }

        const workId = term.workId ?? currentTerm?.workId;
        if (!workId) return;
        if (selectedWork?.id !== workId) dispatch(fetchWork(workId));
        if (workTermsWorkId !== workId) dispatch(fetchWorkTerms(workId));
    }, [term?.id, term?.workId, currentTerm?.workId, selectedWork?.id, workTermsWorkId]);

    useEffect(() => {
        if (!term?.id) return;
        if (!termValidationData) return setCurrentTab(0);
        dispatch(
            fetchAgreementGenerated({
                elementId: term.termId || term.id,
                templateId: termValidationData.templateId,
                from: "book",
            }),
        );
    }, [term?.id]);

    const { periods } = useAppSelector((state) => state.period);
    const [hasReadyOnce, setHasReadyOnce] = useState(false);

    useEffect(() => {
        setHasReadyOnce(false);
    }, [currentTerm?.id]);

    const workId = term?.workId ?? currentTerm?.workId;
    const associatedTermsReady =
        from !== "beneficiars" || workId == null || (workTermsStatus === "succeeded" && workTermsWorkId === workId);

    const allInitialDataReady =
        termStatus === "succeeded" &&
        agreementsStatus === "succeeded" &&
        associatedTermsReady &&
        !!term?.title &&
        periods.length > 0;

    useEffect(() => {
        if (allInitialDataReady) setHasReadyOnce(true);
    }, [allInitialDataReady]);

    const isInfoTabLoading = !hasReadyOnce;

    const handleCreateAvenant = () => {
        const { beneficiarId: beneficiaryId, title, id, titleId, workId, author, firstname, lastname } = currentTerm;
        const beneficiary =
            author ||
            [firstname, lastname].filter(Boolean).join(" ") ||
            [currentBeneficiary?.name, currentBeneficiary?.surname].filter(Boolean).join(" ");
        navigate("/agreements/new", {
            state: {
                relatedResourceData: {
                    relatedResource: "term",
                    relatedResourceId: id,
                    beneficiaryId,
                    titleId,
                    workId: workId ?? term?.workId,
                    title,
                    beneficiary,
                },
                documentType: "avenant",
                signatureProvider: activeSignProvider.key,
            } as NewAgreementLocationState,
        });
    };

    const newDocumentMenuActions: Action[] = [
        {
            name: t("ContractSettings.SendForSignatureViaProvider", { provider: activeSignProvider.label }),
            action: handleShowAgreementModal,
        },
        {
            name: t("ShowDetails.CreateAvenant"),
            action: handleCreateAvenant,
        },
        {
            name: t("ContractSettings.AddManuallySignedContract"),
            action: handleShowUploadModal,
        },
    ];

    return (
        <div>
            <ExitModal
                open={blockModal}
                onConfirm={() => {
                    setEditMode(false);
                    setBlockModal(false);
                    setIsDirty(false);
                }}
                onCancel={() => setBlockModal(false)}
            />
            <DeleteModal
                elementTitle={t("ShowDetails.ThisNote")}
                open={deleteNoteModal}
                onClose={() => setDeleteNoteModal(false)}
                onDelete={handleDeleteNote}
            />
            <Drawer anchor={"right"} open={isOpen} onClose={handleClosingDrawer}>
                {term && !editMode && (
                    <div className="show-details">
                        <div className="show-details-header">
                            <div className="show-details-title">
                                {termStatus === "pending" ? (
                                    <Skeleton width={"26rem"} height={"2rem"} />
                                ) : (
                                    <h1>
                                        {term.title} {"<->"} {term.author}
                                    </h1>
                                )}
                            </div>
                            <div className="show-details-close">
                                <StyledButton buttonType="primary" onClick={() => setEditMode(true)}>
                                    {t("ShowDetails.EditTerm")}
                                </StyledButton>
                                <ButtonBase
                                    variant="text"
                                    styles={{ border: "none", width: "unset", height: "1.9rem" }}
                                    onClick={() => setOpen(false)}
                                >
                                    <CloseIcon sx={{ color: "black" }} className="term-details-close" />
                                </ButtonBase>
                            </div>
                        </div>
                        <div className="show-details-content">
                            <StyledTabs value={currentTab} onChange={handleChange} style={{ paddingLeft: 40 }}>
                                {/* the style removes padding left and right like in the figma, but the tab has a min-width so it looks weird when some tabs don't have padding and some have min-width that adds space, like a padding */}
                                {/* style={{paddingLeft: 1, paddingRight: 1}} */}
                                <Tab label={t("Summary.Information")} value={0}></Tab>
                                <Tab label={t("Summary.Notes")} value={1}></Tab>
                                {termValidationData && <Tab label={t("Summary.Validation")} value={2}></Tab>}
                                {/* <Tab label={t("Summary.Documents")} value={2}></Tab> */}
                            </StyledTabs>

                            {/* First panel: information */}
                            <TabPanel value={currentTab} index={0} style={{ padding: "0 48px" }}>
                                {isInfoTabLoading ? (
                                    <div style={{ display: "flex", justifyContent: "center", padding: "3rem 0" }}>
                                        <Spinner />
                                    </div>
                                ) : (
                                    <>
                                        {(() => {
                                            const titleName =
                                                dataForRecap.work?.name || dataForRecap.title?.name || term.title || "";
                                            const titleCover =
                                                coverUrlForWork(dataForRecap.work) || dataForRecap.title?.avatarUrl;
                                            const beneficiaryName =
                                                [dataForRecap.beneficiary?.name, dataForRecap.beneficiary?.surname]
                                                    .filter(Boolean)
                                                    .join(" ") ||
                                                term.author ||
                                                "";
                                            return (
                                                <div className="main-info-recap">
                                                    <div className="contract-title">
                                                        <div className="left">
                                                            <Avatar
                                                                src={titleCover}
                                                                variant="rounded"
                                                                className="term-recap-avatar"
                                                                sx={{
                                                                    width: "30px",
                                                                    height: "45px",
                                                                    bgcolor: "#EEF3FD",
                                                                    border: "1px solid #EAEAEA",
                                                                }}
                                                            >
                                                                {!titleCover && titleName.slice(0, 1).toUpperCase()}
                                                            </Avatar>
                                                            <h2 id="recap-title-name">{titleName}</h2>
                                                        </div>
                                                        <DoubleArrow color={variables["foreground"]} />
                                                        <div className="right">
                                                            <Avatar
                                                                src={dataForRecap.beneficiary?.avatarUrl}
                                                                className="term-recap-avatar"
                                                                sx={{
                                                                    width: "30px",
                                                                    height: "30px",
                                                                    bgcolor: "#F6F4FC",
                                                                    border: "1px solid #EAEAEA",
                                                                }}
                                                                imgProps={{ sx: { objectFit: "contain" } }}
                                                            >
                                                                {!dataForRecap.beneficiary?.avatarUrl &&
                                                                    beneficiaryName.slice(0, 1).toUpperCase()}
                                                            </Avatar>
                                                            <h2 id="recap-beneficiary-name">{beneficiaryName}</h2>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                        {usesAgreements && !termValidationData && (
                                            <ContractsCounterBlock
                                                agreements={termAgreements}
                                                resourceType="term"
                                                resourceId={term.termId ?? term.id}
                                                parentId={term.beneficiarId ?? undefined}
                                                workId={term.workId ?? undefined}
                                                titleId={term.titleId ?? undefined}
                                                primaryAction={
                                                    <ButtonMenu
                                                        mainActions={{
                                                            icon: <Plus />,
                                                            name: t("ContractSettings.NewDocumentForSignature"),
                                                        }}
                                                        actions={newDocumentMenuActions}
                                                        showExpandBtn={false}
                                                        fullWidth
                                                    />
                                                }
                                            />
                                        )}

                                        <Collapse in={term?.originalFilename !== null} timeout={"auto"}>
                                            <TermDetail company={company} term={term}></TermDetail>
                                        </Collapse>

                                        {usesAgreements ||
                                            (!usesAgreements && term?.originalFilename !== null && (
                                                <H3>{t("ShowDetails.ContractOnCrealo")}</H3>
                                            ))}
                                        <TermRecap
                                            data={dataForRecap}
                                            chosenChannels={filteredChannels}
                                            channels={channels}
                                            usesGroups={usesChannelGroups}
                                            chosenGroups={groupsWithChannels}
                                            usesAdvanceCompensation={usesAdvanceGroups}
                                            chosenAdvanceGroup={term?.compensationGroupId === null ? null : termGroup}
                                            showAdvanceGroupTitles
                                            advancePaymentExists={!!linkedAdvancePayment}
                                            usesInitialAdvance={usesInitialAdvance}
                                            usesCumulativeRoyalties={usesCumulativeRoyalties}
                                            channelsInGroups={channelsInGroups}
                                            mode="edit"
                                            from="drawer"
                                            term={term}
                                            termAdvancePayments={termAdvancePayments}
                                            coveredTitleIds={term?.coveredTitleIds}
                                        />

                                        {!usesAgreements && term?.originalFilename == null && (
                                            <div style={{ marginTop: "2rem" }}>
                                                <TermDetail company={company} term={term}></TermDetail>
                                            </div>
                                        )}
                                    </>
                                )}
                            </TabPanel>

                            {/* Second panel: notes */}
                            <TabPanel value={currentTab} index={1}>
                                <div className="show-details-notes">
                                    {!isAddingNote && (
                                        <div className="addNewNote" onClick={handleAddingNote}>
                                            <Plus />
                                            <span>{t("ShowDetails.AddNote")}</span>
                                        </div>
                                    )}
                                    {isAddingNote && (
                                        <div className="show-details-write-note">
                                            <RichEditor onChange={onEditorChange} />
                                        </div>
                                    )}
                                    {notes &&
                                        notes.length > 0 &&
                                        notes.map((item: INotes, i: any) => (
                                            <div className="show-details-read-notes" key={`show-details-notes-${i}`}>
                                                <div
                                                    className="show-details-note-delete"
                                                    onClick={() => handleOpenDeleteNoteModal(item.id!)}
                                                >
                                                    <Basket />
                                                </div>
                                                <div className="show-details-note-date">
                                                    {formatLocaleDateString({ date: item.created! })}
                                                </div>
                                                <div className="show-details-note-title">{item.title}</div>
                                                <RichEditor
                                                    readOnly={true}
                                                    readOnlyEditorState={JSON.parse(item.description)}
                                                    onChange={() => {}}
                                                />
                                            </div>
                                        ))}
                                </div>
                            </TabPanel>
                            <TabPanel value={currentTab} index={2} style={{ margin: "0" }}>
                                <div className="show-details-validation">
                                    <div className="show-details-validation-header">
                                        {!location.pathname.includes("validation") && (
                                            <InfoBlue
                                                info={
                                                    <Body color="info">
                                                        {t("ContractValidation.WaitingForValidation")}
                                                    </Body>
                                                }
                                            />
                                        )}
                                        <H3 color="grey">{t("ContractValidation.ValidationChain")}</H3>
                                        <div
                                            className="agreement-step-2-selected-beneficiary-list"
                                            style={{ margin: "0 48px" }}
                                        >
                                            {validationList[term?.termId ?? term?.id]?.validationList?.map(
                                                (item: any, i: number) => (
                                                    <div
                                                        className="agreement-step-2-selected-beneficiary-element"
                                                        key={i}
                                                    >
                                                        <div className="left">
                                                            <Avatar
                                                                src={item?.avatarUrl}
                                                                variant="circular"
                                                                sx={{
                                                                    width: "32px",
                                                                    height: "32px",
                                                                    backgroundColor: "#EEF3FD",
                                                                    color: variables["foreground"],
                                                                    fontSize: "14px",
                                                                }}
                                                            >
                                                                {getInitials(item?.name ?? "")}
                                                            </Avatar>
                                                            <Body>{item?.name ?? ""}</Body>
                                                        </div>
                                                        <SmallBody color="grey">{item?.email ?? ""}</SmallBody>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                        <H3 color="grey" style={{ marginLeft: "0 48px !important" }}>
                                            {t("ContractValidation.Preview")}
                                        </H3>
                                    </div>
                                    {sendAgreementStatus === "pending" ? (
                                        <div style={{ width: "100%", textAlign: "center" }}>
                                            <Spinner />
                                        </div>
                                    ) : (
                                        agreementGenerated && (
                                            <>
                                                <div
                                                    className="agreement-step-1-choose-model-template-content"
                                                    dangerouslySetInnerHTML={{
                                                        __html: agreementGeneratedWithUserVariables,
                                                    }}
                                                />
                                            </>
                                        )
                                    )}
                                </div>
                            </TabPanel>
                            {/* <TabPanel
                value={currentTab}
                index={2}
              >
                <div className="feature-not-ready">
                  {t("Misc.FeatureInProgress")}
                </div>
              </TabPanel> */}
                        </div>
                    </div>
                )}

                {term && editMode && (
                    <div className="term-details-edit">
                        <Close className="edit-close-btn" onClick={handleCloseWithModal} />
                        <TermFormContainer
                            mode="edit"
                            from={from}
                            currentTerm={term}
                            groups={groupsWithChannels}
                            onEdit={() => setEditMode(false)}
                            onDelete={(termId) => handleDeleteTerm(termId)}
                            onDirty={(isDirty) => setIsDirty(isDirty)}
                            linkedAdvancePayment={linkedAdvancePayment}
                        />
                    </div>
                )}

                {showAgreementModal && (
                    <AgreementModal
                        open={showAgreementModal}
                        article={currentTerm as ITerm}
                        onClose={() => setShowAgreementModal(false)}
                        onSend={() => dispatch(fetchTerm(currentTerm.id))}
                        from="book"
                        relatedResource="term"
                        initialDocumentType="agreement"
                    />
                )}

                <ModalBox
                    open={showDocumentSign}
                    onClose={() => setShowDocumentSign(false)}
                    mainTitle={t("Articles.AddManually")}
                >
                    <div className="article-detail-content-signature-modal">
                        <div className="main-text">{t("Articles.SendSignatureManualText")}</div>
                        {/* <div className="sub-text">
                    {t("Articles.SendSignatureManualSubText")}
                    <span onClick={() => setManualUpload(false)}>{t("Articles.SendSignatureManualValidateText")}</span>.
                </div> */}
                        <div className="separator"></div>
                        <div style={{ display: "flex", flexFlow: "column", gap: "16px" }}>
                            <DropZoneCSV
                                setFile={setFile}
                                file={file}
                                acceptedFilesTypeList={{ "application/pdf": [".pdf"] }}
                            />
                            <div>
                                <h3>{t("Articles.SignatureDate")}</h3>
                                <StyledDatePicker
                                    value={signatureDate}
                                    onChange={(value: any) => setSignatureDate(value)}
                                    inputFormat="dd/MM/yyyy"
                                    renderInput={(props) => <StyledTextField {...props} fullWidth />}
                                />
                            </div>
                        </div>
                        <div className="article-detail-content-signature-modal-actions">
                            <StyledButton buttonType="secondary" fullWidth onClick={() => setShowDocumentSign(false)}>
                                {t("Articles.Cancel")}
                            </StyledButton>
                            <StyledButton
                                buttonType="primary"
                                fullWidth
                                onClick={sendForSignature}
                                disabled={!file?.length || isLoading}
                                loading={isLoading}
                            >
                                {!isLoading && (
                                    <>{manualUpload ? t("Articles.Save") : t("Articles.SendForSignature")}</>
                                )}
                            </StyledButton>
                        </div>
                    </div>
                </ModalBox>
            </Drawer>
        </div>
    );
};

function TabPanel(props: any) {
    const { children, value, index, ...other } = props;

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: "25px",
                marginTop: "20px",
            }}
            role={"tabpanel"}
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && <>{children}</>}
        </div>
    );
}

export default ShowDetails;
